// Reports — figures a partner can hand to somebody else.
//
// Five that answer a question, rather than a builder for making your
// own. The firm asked for reports; a builder is its own product and
// nobody configures it.
//
// EVERY REPORT SHOWS ITS LIMITS ON THE PAGE, not behind a link. A cost
// total that omits anything recorded outside the app is a floor, not a
// total, and a partner forwarding it has no way to know unless it is
// written next to the number. The caveats travel into the CSV too,
// because the forwarded copy is the one most likely to be read without
// context.
//
// No severity colours. These are counts and sums; which of them is
// alarming is the reader's judgment, not the app's.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch, API } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const RANGES = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

// Which reports take a period, and which are a snapshot of right now.
const RANGED = { activity: "days", settlements: "days",
                 deadline_exposure: "within_days" };

function money(n) {
  return (n == null) ? "—"
    : n.toLocaleString(undefined, { style: "currency", currency: "USD",
                                    maximumFractionDigits: 0 });
}

export function ReportsPage({ onMatterClick }) {
  const [index, setIndex] = useState(null);
  const [active, setActive] = useState("case_inventory");
  const [days, setDays] = useState(90);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiFetch("/api/reports").then(setIndex).catch((e) => setError(e.message));
  }, []);

  const load = useCallback(async () => {
    setPayload(null);
    setError(null);
    try {
      const param = RANGED[active];
      const qs = param ? `?${param}=${days}` : "";
      setPayload(await apiFetch(`/api/reports/${active}${qs}`));
    } catch (e) {
      setError(e.message);
    }
  }, [active, days]);

  useEffect(() => { load(); }, [load]);

  const tables = payload
    ? Object.entries(payload).filter(
        ([, v]) => Array.isArray(v) && v.length && typeof v[0] === "object")
    : [];

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "1000px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "32px",
               color: C.navy, marginBottom: "6px" },
    }, "Reports"),
    React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "20px" },
    }, "Firm-wide figures. Every number is computed from the app's own "
     + "records — nothing here is written by a model."),

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px" },
    }, error),

    // Which report.
    React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" },
    },
      ...((index?.reports) || []).map((r) => React.createElement("button", {
        key: r.key,
        onClick: () => setActive(r.key),
        title: r.question,
        style: {
          border: `1px solid ${active === r.key ? C.navy : C.warmGray}`,
          background: active === r.key ? C.navy : "#fff",
          color: active === r.key ? "#fff" : C.navy,
          borderRadius: "7px", padding: "7px 14px", fontSize: "12.5px",
          fontWeight: 600, cursor: "pointer",
        },
      }, r.title)),
    ),

    // Period, only where one applies.
    RANGED[active] && React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "8px", alignItems: "center",
               marginBottom: "18px" },
    },
      React.createElement("span", {
        style: { fontSize: "12px", color: C.textMuted },
      }, active === "deadline_exposure" ? "Looking ahead" : "Covering the last"),
      ...RANGES.map((r) => React.createElement("button", {
        key: r.days,
        onClick: () => setDays(r.days),
        style: {
          border: `1px solid ${days === r.days ? C.navy : C.warmGray}`,
          background: days === r.days ? C.navy : "#fff",
          color: days === r.days ? "#fff" : C.navy,
          borderRadius: "6px", padding: "5px 11px", fontSize: "12px",
          fontWeight: 600, cursor: "pointer",
        },
      }, r.label)),
    ),

    payload === null && !error
      ? React.createElement("div", {
          style: { display: "flex", gap: "8px", alignItems: "center",
                   padding: "30px 0" },
        },
          React.createElement(Spinner, { size: 15 }),
          React.createElement("span", {
            style: { fontSize: "13px", color: C.textMuted },
          }, "Working it out…"))
      : payload && React.createElement("div", null,

          // Headline figures.
          payload.totals && React.createElement("div", {
            className: "wrap-row",
            style: { display: "flex", gap: "28px", flexWrap: "wrap",
                     padding: "6px 0 18px",
                     borderBottom: `1px solid ${C.warmGray}`,
                     marginBottom: "18px" },
          },
            ...Object.entries(payload.totals).map(([k, v]) =>
              React.createElement("div", { key: k },
                React.createElement("div", {
                  style: { fontSize: "22px", fontWeight: 700, color: C.navy,
                           fontFamily: "'Playfair Display',serif",
                           fontVariantNumeric: "tabular-nums" },
                }, /amount|gross|outstanding|advanced|recovered|written|average/
                     .test(k) ? money(v) : String(v)),
                React.createElement("div", {
                  style: { fontSize: "11px", color: C.textMuted,
                           textTransform: "uppercase", letterSpacing: "0.06em" },
                }, k.replace(/_/g, " ")),
              )),
          ),

          // The limits, next to the numbers rather than behind a link.
          (payload.caveats || []).length > 0 && React.createElement("div", {
            style: { background: C.warmWhite, border: `1px solid ${C.warmGray}`,
                     borderRadius: "9px", padding: "12px 15px",
                     marginBottom: "20px", fontSize: "12.5px",
                     color: C.textMuted, lineHeight: 1.6 },
          },
            React.createElement("div", {
              style: { fontWeight: 700, color: C.navy, marginBottom: "3px" },
            }, "What this does not include"),
            React.createElement("ul", { style: { margin: "0 0 0 17px", padding: 0 } },
              ...payload.caveats.map((c, i) =>
                React.createElement("li", { key: i }, c)),
            ),
          ),

          ...tables.map(([name, rows]) => React.createElement(Table, {
            key: name, name, rows, reportKey: active,
            onMatterClick,
          })),

          tables.length === 0 && React.createElement("div", {
            style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                     borderRadius: "10px", padding: "22px", fontSize: "13.5px",
                     color: C.textMuted, lineHeight: 1.6 },
          }, "Nothing to list for this period. The limits above say what "
           + "would and would not have appeared."),
        ),

    // Named rather than absent — a missing report reads as an
    // oversight, a named one with a reason reads as a decision.
    (index?.blocked || []).length > 0 && React.createElement("div", {
      style: { marginTop: "34px", paddingTop: "18px",
               borderTop: `1px solid ${C.warmGray}` },
    },
      React.createElement("h2", {
        style: { fontSize: "13px", color: C.navy, fontWeight: 700,
                 textTransform: "uppercase", letterSpacing: "0.06em",
                 marginBottom: "10px" },
      }, "Not available yet"),
      ...index.blocked.map((b, i) => React.createElement("div", {
        key: i,
        style: { fontSize: "13px", color: C.textMuted, lineHeight: 1.6,
                 marginBottom: "12px" },
      },
        React.createElement("strong", { style: { color: C.textDark } }, b.name),
        " — ", b.blocked_by,
        React.createElement("div", { style: { fontSize: "12px" } }, b.detail),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textLight },
        }, "Unblocked by: ", b.unblocked_by),
      )),
    ),
  );
}


function Table({ name, rows, reportKey, onMatterClick }) {
  const [showAll, setShowAll] = useState(false);
  const cols = Object.keys(rows[0]);
  const shown = showAll ? rows : rows.slice(0, 15);

  return React.createElement("div", { style: { marginBottom: "26px" } },
    React.createElement("div", {
      style: { display: "flex", alignItems: "baseline", gap: "10px",
               marginBottom: "8px" },
    },
      React.createElement("h2", {
        style: { fontSize: "13px", color: C.navy, fontWeight: 700,
                 textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 },
      }, name.replace(/_/g, " ")),
      React.createElement("div", { style: { flex: 1, height: "1px", background: C.warmGray } }),
      React.createElement("span", {
        style: { fontSize: "11px", color: C.textLight },
      }, `${rows.length} row${rows.length === 1 ? "" : "s"}`),
      // Straight to a spreadsheet, caveats included as comment lines.
      React.createElement("a", {
        href: `${API}/api/reports/${reportKey}?fmt=csv&section=${name}`,
        style: { fontSize: "11.5px", color: C.navy },
      }, "CSV"),
    ),

    React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}`, borderRadius: "9px",
               background: "#fff", overflowX: "auto" },
    },
      React.createElement("table", {
        style: { width: "100%", borderCollapse: "collapse", fontSize: "12.5px" },
      },
        React.createElement("thead", null,
          React.createElement("tr", null,
            ...cols.map((c) => React.createElement("th", {
              key: c,
              style: { textAlign: /amount|count|outstanding|days/.test(c)
                         ? "right" : "left",
                       padding: "9px 12px", color: C.textMuted,
                       fontWeight: 600, fontSize: "11px",
                       textTransform: "uppercase", letterSpacing: "0.05em",
                       borderBottom: `1px solid ${C.warmGray}`,
                       whiteSpace: "nowrap" },
            }, c.replace(/_/g, " "))),
          ),
        ),
        React.createElement("tbody", null,
          ...shown.map((r, i) => React.createElement("tr", { key: i },
            ...cols.map((c) => {
              const v = r[c];
              const isMatter = c === "matter_id" && v;
              return React.createElement("td", {
                key: c,
                style: {
                  padding: "9px 12px", color: C.textDark,
                  borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}55`,
                  textAlign: /amount|count|outstanding|days/.test(c)
                    ? "right" : "left",
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                },
              },
                isMatter
                  ? React.createElement("button", {
                      onClick: () => onMatterClick
                        && onMatterClick({ id: Number(v) }),
                      style: { border: "none", background: "transparent",
                               padding: 0, color: C.navy, cursor: "pointer",
                               textDecoration: "underline", fontSize: "12.5px" },
                    }, String(v))
                  : /amount|gross|outstanding/.test(c) && typeof v === "number"
                    ? money(v)
                    : (v == null ? "—" : String(v)));
            }),
          )),
        ),
      ),
    ),

    rows.length > 15 && React.createElement("button", {
      onClick: () => setShowAll((s) => !s),
      style: { border: "none", background: "transparent", padding: "7px 0",
               fontSize: "12px", color: C.navy, cursor: "pointer",
               textDecoration: "underline" },
    }, showAll ? "Show fewer"
               : `Show all ${rows.length} — the CSV has them either way`),
  );
}
